/**
 * Heuristiques client-side pour le mode audit /admin/commissions.
 * Lecture seule — aucune écriture, aucun recalcul. Aligné sur audit_commissions_duplicates.py.
 */

const BACKFILL_NOTE = 'retroactive 30d backfill';

/** @returns {boolean} */
export function isBackfillLine(item) {
  return BACKFILL_NOTE === (item?.notes || '').trim().toLowerCase();
}

/**
 * Type métier détecté depuis la référence transaction (heuristique).
 * @returns {'vente'|'acompte_reparation'|'acompte_commande'|'reparation'|'commande'|'indetermine'}
 */
export function detectRealOperationType(transactionId) {
  const r = (transactionId || '').toUpperCase();
  if (!r) return 'indetermine';

  if (r.startsWith('DEV-AC-') || (r.includes('REM') && r.includes('AC'))) {
    return 'acompte_reparation';
  }
  if (r.startsWith('CMD-AC-') || (r.startsWith('ACOMPTE-') && !r.includes('REM'))) {
    return 'acompte_commande';
  }
  if (r.startsWith('CMD-')) return 'commande';
  if (/^REM\d+/.test(r) || (r.includes('REM') && !r.startsWith('POS-'))) {
    return 'reparation';
  }
  if (r.startsWith('POS-ACOMPTE-REP-')) return 'acompte_reparation';
  if (r.startsWith('POS-ACOMPTE-CMD-') || r.startsWith('POS-ACOMPTE-COM-')) {
    return 'acompte_commande';
  }
  if (r.startsWith('POS-')) return 'vente';
  return 'indetermine';
}

/** Commission type cible métier Carlos (3 types principaux). */
export function expectedCommissionType(realOp) {
  switch (realOp) {
    case 'acompte_reparation':
    case 'reparation':
      return 'reparations';
    case 'acompte_commande':
    case 'commande':
      return 'commandes';
    case 'vente':
      return 'ventes';
    default:
      return null;
  }
}

/**
 * Source de génération affichée.
 * @returns {'POS'|'Réparation'|'Commande'|'Backfill'|'Autre'}
 */
export function detectGenerationSource(item) {
  if (isBackfillLine(item)) return 'Backfill';
  const ref = (item?.transaction_id || '').toUpperCase();
  if (ref.startsWith('DEV-AC-') || ref.startsWith('POS-ACOMPTE-REP-')) return 'Réparation';
  if (ref.startsWith('CMD-AC-') || ref.startsWith('CMD-') || ref.startsWith('POS-ACOMPTE-CMD-')) {
    return 'Commande';
  }
  if (ref.startsWith('POS-')) return 'POS';
  if (ref.includes('REM')) return 'Réparation';
  return 'Autre';
}

const REAL_OP_LABELS = {
  vente: 'Vente POS',
  acompte_reparation: 'Acompte réparation',
  acompte_commande: 'Acompte commande',
  reparation: 'Réparation',
  commande: 'Commande',
  indetermine: 'Indéterminé',
};

export function realOperationLabel(realOp) {
  return REAL_OP_LABELS[realOp] || realOp;
}

/** Ligne potentiellement mal classée (type ledger ≠ type métier attendu). */
export function isMisclassifiedLine(item) {
  const realOp = detectRealOperationType(item?.transaction_id);
  const expected = expectedCommissionType(realOp);
  if (!expected) return false;
  return (item?.type || '') !== expected;
}

export function getCurrentRateForType(staffConfig, commissionType) {
  const cfg = staffConfig?.[commissionType];
  if (!cfg) return { percent: 0, fixed: 0 };
  return {
    percent: Number(cfg.rate_percent) || 0,
    fixed: Number(cfg.rate_fixed) || 0,
  };
}

/** Taux appliqué ≠ config actuelle du collaborateur pour ce type ledger. */
export function isHistoricalSnapshot(item, staffConfig) {
  if (!staffConfig || !item?.type) return false;
  const cur = getCurrentRateForType(staffConfig, item.type);
  const appliedPct = Number(item.rate_percent_used) || 0;
  const appliedFixed = Number(item.rate_fixed_used) || 0;
  return Math.abs(appliedPct - cur.percent) > 0.001 || Math.abs(appliedFixed - cur.fixed) > 0.001;
}

/**
 * Badge « Snapshot historique » : uniquement si la ligne est une anomalie isolée.
 * Les commissions d'une même période (ex. toutes à 20 % quand la config était à 20 %)
 * ne sont pas badgées — le bandeau en tête de page suffit.
 */
export function isLineRateAnomaly(item, staffConfig, allItems = []) {
  if (!isHistoricalSnapshot(item, staffConfig)) return false;
  if (isBackfillLine(item)) return false;

  const appliedPct = Number(item.rate_percent_used) || 0;
  const cur = getCurrentRateForType(staffConfig, item.type);

  // Taux supérieur à la config actuelle = période historique normale (ex. 20 % → 5 %)
  if (appliedPct > cur.percent + 0.001) return false;

  const cohort = (allItems || []).filter(
    x => x.staff_username === item.staff_username
      && x.type === item.type
      && !isBackfillLine(x)
  );

  if (cohort.length <= 1) return true;

  const rateCounts = new Map();
  for (const x of cohort) {
    const r = Number(x.rate_percent_used) || 0;
    const key = r.toFixed(2);
    rateCounts.set(key, (rateCounts.get(key) || 0) + 1);
  }
  let modeRate = appliedPct;
  let maxCount = 0;
  rateCounts.forEach((count, key) => {
    if (count > maxCount) {
      maxCount = count;
      modeRate = parseFloat(key);
    }
  });

  return Math.abs(appliedPct - modeRate) > 0.001;
}

export function formatRateDisplay(percent, fixed) {
  const parts = [];
  if (percent > 0) parts.push(`${percent}%`);
  if (fixed > 0) parts.push(`${fixed} €`);
  return parts.length ? parts.join(' + ') : '—';
}

export function snapshotTooltipText(item, staffConfig) {
  const cur = getCurrentRateForType(staffConfig, item.type);
  const appliedPct = Number(item.rate_percent_used) || 0;
  const appliedFixed = Number(item.rate_fixed_used) || 0;
  const lines = [
    `Configuration actuelle : ${formatRateDisplay(cur.percent, cur.fixed)}`,
    `Taux appliqué lors de la création : ${formatRateDisplay(appliedPct, appliedFixed)}`,
  ];
  if (item.created_at) {
    lines.push(`Date : ${String(item.created_at).slice(0, 10)}`);
  }
  return lines.join('\n');
}

/** Analyse complète d'une ligne pour le mode audit. */
export function analyzeCommissionLine(item, allItems = []) {
  const realOp = detectRealOperationType(item?.transaction_id);
  const expected = expectedCommissionType(realOp);
  const isMisclassified = expected ? (item?.type || '') !== expected : false;
  const duplicatePair = findPotentialDuplicatePair(item, allItems);
  const tags = buildAuditTags(item, realOp, isMisclassified, duplicatePair);

  return {
    realOperation: realOp,
    realOperationLabel: realOperationLabel(realOp),
    appliedType: item?.type || '—',
    expectedType: expected,
    source: detectGenerationSource(item),
    isBackfill: isBackfillLine(item),
    isMisclassified,
    isSuspectReparation: tags.includes('Suspect réparation'),
    isSuspectCommande: tags.includes('Suspect commande'),
    duplicatePair,
    auditTags: tags,
  };
}

/** Doublon POS + DEV-AC dans le jeu de lignes chargé. */
export function findPotentialDuplicatePair(item, allItems) {
  if (!item || !allItems?.length) return null;
  const ref = (item.transaction_id || '').toUpperCase();
  const amt = Number(item.transaction_amount) || 0;
  const staff = item.staff_username;

  for (const other of allItems) {
    if (other.id === item.id || other.staff_username !== staff) continue;
    if (Math.abs((Number(other.transaction_amount) || 0) - amt) > 0.03) continue;
    const oref = (other.transaction_id || '').toUpperCase();
    const posDev =
      (ref.startsWith('POS-') && oref.startsWith('DEV-AC-')) ||
      (ref.startsWith('DEV-AC-') && oref.startsWith('POS-'));
    if (!posDev) continue;
    const oneBackfill = isBackfillLine(item) || isBackfillLine(other);
    if (oneBackfill || ref.startsWith('DEV-AC-') || oref.startsWith('DEV-AC-')) {
      return {
        otherId: other.id,
        otherTransactionId: other.transaction_id,
        kind: 'pos_plus_dev_ac',
      };
    }
  }
  return null;
}

function buildAuditTags(item, realOp, isMisclassified, duplicatePair) {
  const tags = [];
  if (isBackfillLine(item)) tags.push('Backfill');
  if (duplicatePair) tags.push('Doublon potentiel');
  if (isMisclassified && (realOp === 'acompte_reparation' || realOp === 'reparation')) {
    tags.push('Suspect réparation');
  }
  if (isMisclassified && (realOp === 'acompte_commande' || realOp === 'commande')) {
    tags.push('Suspect commande');
  }
  return tags;
}
